import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { Flex, HStack, VStack } from '@televet/kibble-ui/chakra';
import { Button, Heading, Tag, Text } from '@televet/kibble-ui';
import {
  createFixtureSubmissionAdapter,
  FORMS_ADAPTER_VERSION,
  isFormsAdapterError,
  rejectWithError,
  sampleFormSubmission,
} from '@astro/flow-forms';
import type {
  FixtureFormSubmissionAdapter,
  FormSubmissionAdapter,
  FormSubmissionContext,
  FormSubmissionPet,
} from '@astro/flow-forms';
import { FlowFormSubmission } from '@/features/flowForms/FlowFormSubmission';

interface ReportedError {
  message: string;
  context: Record<string, unknown>;
}

declare global {
  interface Window {
    __formsFixtureAdapter?: FixtureFormSubmissionAdapter;
    __formsReportedErrors?: ReportedError[];
  }
}

const FAILABLE_METHODS = [
  'getSubmission',
  'saveDraft',
  'submit',
  'unsubmit',
  'uploadSignature',
  'exportPdf',
] as const;
type FailableMethod = (typeof FAILABLE_METHODS)[number];

// The router writes this back to the URL and parses it again, so it keeps the URL's own shape.
interface FormsDevSearch {
  submission: string;
  owner: boolean;
  forceEdit: boolean;
  pets?: 'none';
  fail?: string;
}

const FIXTURE_PETS: FormSubmissionPet[] = [
  { id: 'pet-123', name: 'Biscuit', species: 'Canine' },
  { id: 'pet-789', name: 'Mochi', species: 'Feline' },
];

const isFlagOn = (value: unknown): boolean => value === true || value === 'true';

const parseFailures = (value: string | undefined): FailableMethod[] =>
  value ? FAILABLE_METHODS.filter((method) => value.split(',').includes(method)) : [];

interface FormsDevRawSearch {
  submission?: unknown;
  owner?: unknown;
  forceEdit?: unknown;
  pets?: unknown;
  fail?: unknown;
}

// SearchSchemaInput tells the router these raw params are all optional on a Link.
const validateSearch = (search: FormsDevRawSearch & SearchSchemaInput): FormsDevSearch => ({
  submission: typeof search.submission === 'string' ? search.submission : sampleFormSubmission.id,
  owner: search.owner === undefined ? true : isFlagOn(search.owner),
  forceEdit: isFlagOn(search.forceEdit),
  ...(search.pets === 'none' && { pets: 'none' as const }),
  ...(typeof search.fail === 'string' && search.fail !== '' && { fail: search.fail }),
});

const failure = (method: FailableMethod): Promise<never> =>
  rejectWithError({ code: 'NETWORK_ERROR', message: `Fixture ${method} failed on purpose` });

// Calls named in ?fail= reject without reaching the fixture, so they are not recorded.
function withFailures(
  adapter: FixtureFormSubmissionAdapter,
  failing: FailableMethod[]
): FormSubmissionAdapter {
  const fails = (method: FailableMethod): boolean => failing.includes(method);
  return {
    getSubmission: (id) =>
      fails('getSubmission') ? failure('getSubmission') : adapter.getSubmission(id),
    saveDraft: (id, content) =>
      fails('saveDraft') ? failure('saveDraft') : adapter.saveDraft(id, content),
    submit: (id, content) => (fails('submit') ? failure('submit') : adapter.submit(id, content)),
    unsubmit: (id, content) =>
      fails('unsubmit') ? failure('unsubmit') : adapter.unsubmit(id, content),
    uploadSignature: (blob, meta) =>
      fails('uploadSignature') ? failure('uploadSignature') : adapter.uploadSignature(blob, meta),
    exportPdf: (id) => (fails('exportPdf') ? failure('exportPdf') : adapter.exportPdf(id)),
  };
}

const describeError = (error: unknown): string => {
  if (isFormsAdapterError(error)) {
    return `${error.code} ${error.message}`;
  }
  return error instanceof Error ? error.message : String(error);
};

// E2E specs read reported errors back from here; a real host sends them to its error tracker.
const reportFixtureError = (error: unknown, context: Record<string, unknown>): void => {
  const reported = { message: describeError(error), context };
  window.__formsReportedErrors = [...(window.__formsReportedErrors ?? []), reported];
};

function useFixtureHost(search: FormsDevSearch): {
  adapter: FormSubmissionAdapter;
  context: FormSubmissionContext;
} {
  const fixtureAdapter = useMemo(() => createFixtureSubmissionAdapter(), []);
  const adapter = useMemo(
    () => withFailures(fixtureAdapter, parseFailures(search.fail)),
    [fixtureAdapter, search.fail]
  );
  const context = useMemo<FormSubmissionContext>(
    () => ({
      forceEdit: search.forceEdit,
      viewerIsOwner: search.owner,
      pets: search.pets === 'none' ? [] : FIXTURE_PETS,
      reportError: reportFixtureError,
    }),
    [search.forceEdit, search.owner, search.pets]
  );

  // E2E specs read the recorded adapter calls from here, since this page has no database to check.
  useEffect(() => {
    window.__formsFixtureAdapter = fixtureAdapter;
  }, [fixtureAdapter]);

  return { adapter, context };
}

function FormsDevRoute(): React.JSX.Element {
  const search = Route.useSearch();
  const { adapter, context } = useFixtureHost(search);
  const [mountKey, setMountKey] = useState(0);
  const [isClosed, setIsClosed] = useState(false);
  const [submittedResult, setSubmittedResult] = useState<string | null>(null);

  const reopen = (): void => {
    setIsClosed(false);
    setMountKey((key) => key + 1);
  };

  return (
    <VStack align="stretch" gap={4}>
      <HStack justify="space-between">
        <HStack gap={3}>
          <Heading size="md">Pet Portal form</Heading>
          <Tag label="Dev" colorScheme="blue" size="sm" />
          <Tag label={`v${FORMS_ADAPTER_VERSION}`} colorScheme="gray" size="sm" />
        </HStack>
        <Button variant="secondary" onClick={reopen} data-testid="forms-dev-reopen-button">
          Reopen form
        </Button>
      </HStack>
      {submittedResult && <Text data-testid="forms-dev-submitted-result">{submittedResult}</Text>}
      {isClosed ? (
        <Text data-testid="forms-dev-closed-message">The form was closed.</Text>
      ) : (
        <Flex direction="column" h="80vh" borderWidth={1} borderRadius="base">
          <FlowFormSubmission
            key={mountKey}
            formSubmissionId={search.submission}
            adapter={adapter}
            adapterVersion={FORMS_ADAPTER_VERSION}
            context={context}
            onSubmitted={(result): void => setSubmittedResult(JSON.stringify(result))}
            onClose={(): void => setIsClosed(true)}
          />
        </Flex>
      )}
    </VStack>
  );
}

export const Route = createFileRoute('/dev/forms')({
  validateSearch,
  component: FormsDevRoute,
});
